import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Typography, TextField, Box } from "@mui/material";
import { api } from "../utils/api";

const PaginatedTable = ({
  endpoint,
  searchEndpoint,
  columns,
  searchFields = [],
  dateFields = [],
  title = "Data Table",
  onRowClick,
  getRowClassName,
  sx = {},
  initialQuery = {}
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilters, setDateFilters] = useState({});
  const [query, setQuery] = useState(initialQuery);
  const [totalCounts, setTotalCounts] = useState(null);

  // Server-side search function
  const performSearch = async (page = 1, searchQuery = {}) => {
    try {
      setLoading(true);
      const requestBody = {
        query: { ...query, ...searchQuery },
        page: page,
        limit: 100
      };

      console.log("PaginatedTable performing search:", { page, searchQuery, requestBody });

      const response = await api(searchEndpoint || endpoint, searchEndpoint ? "POST" : "GET", requestBody);

      console.log("PaginatedTable search response:", response.data);

      if (response.data.success) {
        const resultData =
          response.data.data.searchResult ||
          response.data.data.allTransactions ||
          response.data.data.allRedemptions ||
          response.data.data.allSubscriptions ||
          [];
        setData(resultData);
        setPagination(response.data.data.pagination);
        setTotalCounts(response.data.data.counts);
        setCurrentPage(page);
        console.log("PaginatedTable updated state:", {
          data: resultData.length,
          pagination: response.data.data.pagination,
          counts: response.data.data.counts
        });
      }
    } catch (error) {
      console.error("Error searching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    performSearch(1, {});
  }, []);

  // Debug useEffect to monitor state changes
  useEffect(() => {
    console.log("PaginatedTable state changed:", {
      currentPage,
      data: data.length,
      pagination,
      loading
    });
  }, [currentPage, data, pagination, loading]);

  // Handle search and filter changes
  useEffect(() => {
    const buildSearchQuery = () => {
      const searchQuery = {};

      // Date filters
      Object.entries(dateFilters).forEach(([field, value]) => {
        if (value) {
          if (!searchQuery[field]) {
            searchQuery[field] = {};
          }
          if (value.from) {
            searchQuery[field].$gte = new Date(value.from).toISOString();
          }
          if (value.to) {
            searchQuery[field].$lte = new Date(value.to).toISOString();
          }
        }
      });

      // Search term filter
      if (searchTerm) {
        searchQuery.searchTerm = searchTerm;
      }

      return searchQuery;
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      const searchQuery = buildSearchQuery();
      performSearch(1, searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, dateFilters, query]);

  const handlePageChange = (event, page) => {
    const searchQuery = {};

    // Rebuild search query for pagination
    Object.entries(dateFilters).forEach(([field, value]) => {
      if (value) {
        if (!searchQuery[field]) {
          searchQuery[field] = {};
        }
        if (value.from) {
          searchQuery[field].$gte = new Date(value.from).toISOString();
        }
        if (value.to) {
          searchQuery[field].$lte = new Date(value.to).toISOString();
        }
      }
    });

    if (searchTerm) {
      searchQuery.searchTerm = searchTerm;
    }

    performSearch(page, searchQuery);
  };

  const handleDateFilterChange = (field, type, value) => {
    setDateFilters((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [type]: value
      }
    }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setDateFilters({});
    setQuery(initialQuery);
    performSearch(1, {});
  };

  return (
    <div className="w-full flex flex-column items-center text-center">
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Typography variant="h6" style={{ marginBottom: "16px" }}>
        {title}
      </Typography>

      {(searchFields.length > 0 || dateFields.length > 0) && (
        <div className="w-full mb-6 px-4 flex flex-col gap-4 items-center">
          {searchFields.length > 0 && (
            <TextField
              fullWidth
              label="Search"
              placeholder={`Search by ${searchFields.join(", ")}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              disabled={loading}
              sx={{ maxWidth: "800px" }}
              InputProps={{
                endAdornment: loading ? (
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      border: "2px solid #f3f3f3",
                      borderTop: "2px solid #3498db",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite"
                    }}
                  ></div>
                ) : null
              }}
            />
          )}

          {dateFields.length > 0 && (
            <div className="flex flex-wrap items-end gap-3 w-full max-w-[800px]">
              {dateFields.map((field) => (
                <Box key={field} sx={{ flex: "1 1 220px" }}>
                  <Typography variant="caption" display="block" sx={{ fontWeight: 600, color: "#555" }}>
                    {field === "createdOnUTC" ? "Created" : field}
                  </Typography>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="date"
                      value={dateFilters[field]?.from || ""}
                      onChange={(e) => handleDateFilterChange(field, "from", e.target.value)}
                      disabled={loading}
                      className="border rounded px-3 py-2 text-sm w-full"
                      style={{ opacity: loading ? 0.6 : 1 }}
                    />
                    <input
                      type="date"
                      value={dateFilters[field]?.to || ""}
                      onChange={(e) => handleDateFilterChange(field, "to", e.target.value)}
                      disabled={loading}
                      className="border rounded px-3 py-2 text-sm w-full"
                      style={{ opacity: loading ? 0.6 : 1 }}
                    />
                  </div>
                </Box>
              ))}

              <button
                onClick={resetFilters}
                disabled={loading}
                className="px-4 py-2 rounded text-white text-sm font-semibold"
                style={{
                  backgroundColor: loading ? "#ccc" : "#f44336",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1
                }}
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}

      {/* Total Counts Display */}
      {totalCounts && (
        <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: "bold", color: "#2e7d32" }}>Valid Records:</span>
                <span
                  style={{
                    backgroundColor: "#e8f5e8",
                    color: "#2e7d32",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "bold"
                  }}
                >
                  {totalCounts.totalActive}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: "bold", color: "#d32f2f" }}>Removed Records:</span>
                <span
                  style={{
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "bold"
                  }}
                >
                  {totalCounts.totalInactive}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontWeight: "bold", color: "#1976d2" }}>Total:</span>
              <span
                style={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontWeight: "bold"
                }}
              >
                {totalCounts.total}
              </span>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            flexDirection: "column",
            gap: "16px"
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}
          ></div>
          <Typography variant="body1" color="textSecondary">
            Loading data...
          </Typography>
        </div>
      ) : data.length > 0 ? (
        <div className="flex flex-column flex-wrap justify-center items-center w-full">
          <Box
            sx={{
              display: "block",
              overflowX: "auto",
              px: { xs: 1, sm: 2 },
              width: { xs: "100%", sm: "fit-content" },
              maxWidth: { xs: "100%", sm: "calc(100vw - 200px)" }, // Account for sidebar width on larger screens
              margin: "0 auto",
              "&::-webkit-scrollbar": {
                height: "8px"
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px"
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
                "&:hover": {
                  background: "#555"
                }
              }
            }}
          >
            <DataGrid
              onRowClick={onRowClick}
              rows={data}
              columns={columns}
              loading={loading}
              getRowId={(row) => row._id || row.id}
              getRowClassName={getRowClassName}
              sx={{
                width: { xs: "100%", sm: "fit-content" },
                minWidth: { xs: "100%", sm: "600px" }, // Full width on mobile, minimum 600px on larger screens
                "& .MuiDataGrid-cell": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  padding: { xs: "8px 4px", sm: "12px 8px" }
                },
                "& .MuiDataGrid-columnHeader": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  padding: { xs: "8px 4px", sm: "8px 8px" }
                },
                ...sx
              }}
            />
          </Box>

          {/* Custom Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <button
                onClick={() => {
                  if (currentPage > 1) {
                    const searchQuery = {};

                    // Rebuild search query for pagination
                    Object.entries(dateFilters).forEach(([field, value]) => {
                      if (value) {
                        if (!searchQuery[field]) {
                          searchQuery[field] = {};
                        }
                        if (value.from) {
                          searchQuery[field].$gte = new Date(value.from).toISOString();
                        }
                        if (value.to) {
                          searchQuery[field].$lte = new Date(value.to).toISOString();
                        }
                      }
                    });

                    if (searchTerm) {
                      searchQuery.searchTerm = searchTerm;
                    }

                    performSearch(currentPage - 1, searchQuery);
                  }
                }}
                disabled={currentPage <= 1}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: currentPage <= 1 ? "#f5f5f5" : "#fff",
                  cursor: currentPage <= 1 ? "not-allowed" : "pointer"
                }}
              >
                Previous
              </button>

              <span style={{ padding: "0 10px" }}>
                Page {currentPage} of {pagination.totalPages}
              </span>

              <button
                onClick={() => {
                  if (currentPage < pagination.totalPages) {
                    const searchQuery = {};

                    // Rebuild search query for pagination
                    Object.entries(dateFilters).forEach(([field, value]) => {
                      if (value) {
                        if (!searchQuery[field]) {
                          searchQuery[field] = {};
                        }
                        if (value.from) {
                          searchQuery[field].$gte = new Date(value.from).toISOString();
                        }
                        if (value.to) {
                          searchQuery[field].$lte = new Date(value.to).toISOString();
                        }
                      }
                    });

                    if (searchTerm) {
                      searchQuery.searchTerm = searchTerm;
                    }

                    performSearch(currentPage + 1, searchQuery);
                  }
                }}
                disabled={currentPage >= pagination.totalPages}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: currentPage >= pagination.totalPages ? "#f5f5f5" : "#fff",
                  cursor: currentPage >= pagination.totalPages ? "not-allowed" : "pointer"
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="error">No records found</p>
      )}
    </div>
  );
};

export default PaginatedTable;
